import React, { useState, useRef, useEffect } from 'react';
import { Share2, Twitter, Facebook, Link2, MessageCircle, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';

export function ShareButton({ 
  title, 
  url = window.location.href,
  description = '',
  image = '',
  variant = 'default', // 'default', 'icon', 'compact'
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const shareOptions = [
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'hover:bg-blue-500/20 hover:text-blue-400',
      action: () => {
        const tweetUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        window.open(tweetUrl, '_blank', 'width=600,height=400');
      }
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'hover:bg-blue-600/20 hover:text-blue-500',
      action: () => {
        const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        window.open(fbUrl, '_blank', 'width=600,height=400');
      }
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'hover:bg-green-500/20 hover:text-green-400',
      action: () => {
        const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${title}\n${url}`)}`;
        window.open(waUrl, '_blank');
      }
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'hover:bg-purple-500/20 hover:text-purple-400',
      action: () => {
        const subject = encodeURIComponent(`Check out: ${title}`);
        const body = encodeURIComponent(`${description}\n\n${url}`);
        const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
        window.open(mailtoLink, '_self');
      }
    },
    {
      name: 'Copy Link',
      icon: copied ? Check : Link2,
      color: copied ? 'bg-green-500/20 text-green-400' : 'hover:bg-[var(--primary)]/20 hover:text-[var(--primary)]',
      action: () => {
        navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      }
    }
  ];

  // Try native share API first on mobile
  const handleShare = async () => {
    if (navigator.share && window.innerWidth < 768) {
      try {
        await navigator.share({
          title,
          text: description,
          url
        });
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          setIsOpen(true);
        }
      }
    } else {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      {variant === 'icon' ? (
        <button
          onClick={handleShare}
          className="p-2 rounded-full bg-[var(--surface)] hover:bg-[var(--primary)]/20 transition-colors"
          data-testid="share-button"
          aria-label="Share"
        >
          <Share2 size={18} />
        </button>
      ) : variant === 'compact' ? (
        <button
          onClick={handleShare}
          className="flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors"
          data-testid="share-button"
        >
          <Share2 size={16} />
          Share
        </button>
      ) : (
        <button
          onClick={handleShare}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--border)] hover:border-[var(--primary)] hover:bg-[var(--primary)]/10 transition-all"
          data-testid="share-button"
        >
          <Share2 size={18} />
          Share
        </button>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-xl p-2 min-w-[180px] z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <p className="text-xs text-[var(--text-secondary)] px-3 py-2 font-medium uppercase tracking-wider">
            Share via
          </p>
          {shareOptions.map((option) => (
            <button
              key={option.name}
              onClick={() => {
                option.action();
                if (option.name !== 'Copy Link') {
                  setIsOpen(false);
                }
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${option.color}`}
              data-testid={`share-${option.name.toLowerCase().replace(' ', '-')}`}
            >
              <option.icon size={18} />
              {option.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ShareButton;
